import React from 'react';
import { FormInputText } from 'components/index';
import { Stack, styled, Typography } from '@mui/material';
import { Control } from 'react-hook-form';
import { LoginFormInputs } from 'features/auth/types';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useCobrandImages } from 'features/cobrand/hooks/useCobrandImage';
import { useCobrand } from 'features/cobrand/hooks/useCobrandUtils';
import { ResponsiveSVG } from 'components/ResponsiveSVG/ResponsiveSVG';

const LoginLogoImage = styled('img')(() => ({
  maxWidth: '296px',
  maxHeight: '100px',
}));

export const LoginStepHeader = ({
  title,
  onLogoClick,
}: {
  title?: string;
  onLogoClick?: () => void;
}) => {
  const { loginLogo } = useCobrandImages();
  const { cobrand } = useCobrand();
  return (
    <>
      {title ? (
        <Typography variant="h5" fontWeight={500}>
          {title}
        </Typography>
      ) : (
        <ResponsiveSVG
          deviceWidth={{ desktop: '296px', mobile: '120px' }}
          svg={loginLogo}
          containerStyle={{ maxHeight: '100px' }}
          alt={`${cobrand.displayName} logo`}
          onClick={onLogoClick}
        />
      )}
    </>
  );
};

export const LoginEmailStep = ({
  control,
  validationRules,
}: {
  // Register options do not have a valid type when used in Controller components
  control: Control<LoginFormInputs, any>;
  validationRules: any;
}) => {
  const { translation } = useTranslationContext();
  return (
    <FormInputText
      id="login-form_email-input"
      name="email"
      control={control}
      rules={validationRules}
      placeholder={translation('email_address')}
      label={translation('login')}
      autoFocus
    />
  );
};

export const LoginPasswordStep = ({
  control,
  email,
  validationRules,
}: {
  control: Control<LoginFormInputs, any>;
  email: string;
  validationRules: any;
}) => {
  const { translation } = useTranslationContext();

  return (
    <Stack spacing={4}>
      <Stack>
        <Typography textAlign="center" variant="button">
          {translation('email_address')}
        </Typography>
        <Typography textAlign="center">{email}</Typography>
      </Stack>
      <FormInputText
        id="login-form_password-input"
        name="password"
        control={control}
        rules={validationRules}
        placeholder={translation('password')}
        label={translation('password')}
        shouldTrim={false}
        secure
        autoFocus
      />
    </Stack>
  );
};

export const LoginForgotPasswordStep = ({
  control,
  validationRules,
}: {
  control: Control<LoginFormInputs, any>;
  validationRules: any;
}) => {
  const { translation } = useTranslationContext();

  return (
    <Stack spacing={4}>
      <Typography variant="body2" textAlign="center">
        {translation('forgot_password_helper_label')}
      </Typography>
      <FormInputText
        id="login-form_forgot-password-input"
        name="forgotPassword"
        control={control}
        rules={validationRules}
        placeholder={translation('email_address')}
        label={translation('email_address')}
        autoFocus
      />
    </Stack>
  );
};

export const LoginPasswordResetConfirmationStep = () => {
  const { translation } = useTranslationContext();

  return (
    <Typography variant="body2" textAlign="center">
      {translation('forgot_password_alert_text')}
    </Typography>
  );
};
